/**
 * HTTP client for NAR Agent Grid auth-grid APIs via the shared api-client.
 *
 * @module api/services/narAgentGrid/narAgentGridService
 */
import { api } from "@risalabs_frontend_org/api-client";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import {
  KillSwitchBulkRequest,
  KillSwitchBulkResponse,
  ListPayerJCodesRequest,
  ListPayerJCodesResponse,
} from "./types";

/**
 * Service for NAR Agent Grid configuration APIs (auth-grid kill switch).
 */
export class NarAgentGridService {
  /**
   * Applies a batch of kill-switch updates for selected auth-grid rows.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Up to {@link KILL_SWITCH_BULK_MAX_ITEMS} kill-switch items
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Per-item and aggregate outcomes from the bulk endpoint
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async killSwitchBulkToggle(
    payload: KillSwitchBulkRequest,
    signal?: AbortSignal,
  ): Promise<KillSwitchBulkResponse> {
    return api.post<KillSwitchBulkRequest, KillSwitchBulkResponse>(
      API_ENDPOINTS.KILL_SWITCH_BULK_TOGGLE,
      payload,
      signal,
    );
  }

  /**
   * Lists distinct J-codes configured for a payer in the auth grid.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Payer name to list J-codes for
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Distinct J-code rows for the payer
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async listJCodesByPayer(
    payload: ListPayerJCodesRequest,
    signal?: AbortSignal,
  ): Promise<ListPayerJCodesResponse> {
    return api.post<ListPayerJCodesRequest, ListPayerJCodesResponse>(
      API_ENDPOINTS.ALL_J_CODES_BY_PAYER,
      payload,
      signal,
    );
  }
}
